import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import {
  sortQRRooms,
  getQRSortDirection,
  QR_SORT_COLUMNS,
  getAvailableActionTypes,
  getAvailableRewardTypes,
  filterActionsByType,
  filterRewardsByType,
  getAllActionTypes,
  getAllRewardTypes,
  sortActions,
  sortRewards,
  ACTION_SORT_COLUMNS,
  REWARD_SORT_COLUMNS,
  getAvailableStayStatuses,
  getAllStayStatusValues,
} from "../../utils/greenMilesUtils";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const DEFAULT_ROOM_TYPE_COLOR = "#4B5563";

const normalizeRoomTypeColor = (value) => {
  if (typeof value !== "string") return DEFAULT_ROOM_TYPE_COLOR;
  let normalized = value.trim();
  if (!normalized) return DEFAULT_ROOM_TYPE_COLOR;
  if (!normalized.startsWith("#")) {
    normalized = `#${normalized}`;
  }
  normalized = normalized.toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(normalized)) {
    return normalized;
  }
  return DEFAULT_ROOM_TYPE_COLOR;
};

export const useGreenMilesHotelData = (hotelId, period = "all") => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [actions, setActions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(period);

  // État pour le tri des chambres QR
  const [sortState, setSortState] = useState({
    column: QR_SORT_COLUMNS.ROOM_NUMBER,
    direction: "asc",
  });

  // États pour le filtrage par type des actions
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [availableActionTypes, setAvailableActionTypes] = useState([]);

  // États pour le filtrage par type des rewards
  const [selectedRewardTypes, setSelectedRewardTypes] = useState([]);
  const [availableRewardTypes, setAvailableRewardTypes] = useState([]);

  // États pour le filtrage par statut de stay
  const [selectedStayStatuses, setSelectedStayStatuses] = useState([]);

  // 🔧 Refs pour éviter les re-fetches intempestifs
  const lastFetchRef = useRef(0);
  const isInitializedRef = useRef(false);
  const currentTokenRef = useRef(token);
  const abortControllerRef = useRef(null);

  // 📊 Mémoriser le token pour éviter les changements constants
  const stableToken = useMemo(() => {
    // Ne mettre à jour le token que s'il a vraiment changé de manière significative
    if (token && token !== currentTokenRef.current) {
      currentTokenRef.current = token;
      return token;
    }
    return currentTokenRef.current;
  }, [token]);

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId || !stableToken) return;
    try {
      setRoomTypesLoading(true);
      const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/room-types`, {
        headers: {
          Authorization: `Bearer ${stableToken}`,
        },
      });
      const data = await response.json();
      if (response.ok && (data?.success === true || data?.success === "OK")) {
        const list = Array.isArray(data?.result) ? data.result : [];
        setRoomTypes(
          list.map((type) => ({
            ...type,
            color: type.color ? normalizeRoomTypeColor(type.color) : null,
          }))
        );
      } else if (response.status !== 404) {
        throw new Error(data?.message || "Impossible de récupérer les types de chambre");
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
    } finally {
      setRoomTypesLoading(false);
    }
  }, [hotelId, stableToken]);

  const fetchHotelData = useCallback(
    async (periodToUse = selectedPeriod, isPeriodChange = false) => {
      if (!hotelId || !stableToken) return;

      // Annuler la requête précédente si elle existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Créer un nouveau controller pour cette requête
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        // Différencier le loading selon le type de changement
        if (isPeriodChange) {
          setPeriodLoading(true);
        } else {
          setLoading(true);
        }
        setError(null);

        // Récupérer les données de base de l'hôtel (avec chambres QR)
        const hotelResponse = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}?period=${periodToUse}`, {
          headers: {
            Authorization: `Bearer ${stableToken}`,
          },
          signal,
        });
        const hotelData = await hotelResponse.json();

        if (hotelData.success !== "OK" || !hotelData.result) {
          throw new Error(hotelData.message || "Erreur lors de la récupération des données de l'hôtel");
        }

        const hotelInfo = hotelData.result;

        // Récupérer les actions détaillées
        const actionsResponse = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/actions`, {
          headers: {
            Authorization: `Bearer ${stableToken}`,
          },
          signal,
        });
        const actionsData = await actionsResponse.json();

        // Récupérer les rewards détaillées
        const rewardsResponse = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/rewards`, {
          headers: {
            Authorization: `Bearer ${stableToken}`,
          },
          signal,
        });
        const rewardsData = await rewardsResponse.json();

        // Traitement des données
        setHotel({
          id: hotelInfo.hId,
          name: hotelInfo.hName,
          address: hotelInfo.hAddress,
          city: hotelInfo.hCity,
          country: hotelInfo.hCountry,
          stars: hotelInfo.hStars,
          visible: hotelInfo.hVisible,
          rooms: hotelInfo.hNbRooms,
          installationDate: hotelInfo.installationDate ?? null,
          previewAccessToken: hotelInfo.hPreviewAccessToken ?? null,
        });

        // Stocker toutes les chambres (avec ou sans QR code)
        const roomsData = hotelInfo.rooms || [];
        setRooms(roomsData);

        // Initialiser les statuts de stay sélectionnés si pas encore fait (sélectionner tous par défaut)
        setSelectedStayStatuses((prev) => {
          if (prev.length === 0 && roomsData.length > 0) {
            const allStayStatuses = getAllStayStatusValues(roomsData);
            return allStayStatuses;
          }
          return prev;
        });

        setStatistics({
          // Séjours et utilisateurs
          total_stays: hotelInfo.statistics.total_stays || 0,
          unique_users: hotelInfo.statistics.unique_users || 0,

          // Actions
          total_actions_collected: hotelInfo.statistics.total_actions_collected || 0,
          stays_with_actions: hotelInfo.statistics.stays_with_actions || 0,
          avg_actions_per_user: hotelInfo.statistics.avg_actions_per_user || 0,
          avg_actions_per_active_user: hotelInfo.statistics.avg_actions_per_active_user || 0,
          actions_count: hotelInfo.statistics.actions_count || 0,

          // Récompenses
          total_rewards_claimed: hotelInfo.statistics.total_rewards_claimed || 0,
          total_rewards_used: hotelInfo.statistics.total_rewards_used || 0,
          stays_with_rewards: hotelInfo.statistics.stays_with_rewards || 0,
          stays_with_used_rewards: hotelInfo.statistics.stays_with_used_rewards || 0,
          rewards_count: hotelInfo.statistics.rewards_count || 0,

          // QR Codes et scans
          total_qr_scans: hotelInfo.statistics.total_qr_scans || 0,
          unique_qr_visitors: hotelInfo.statistics.unique_qr_visitors || 0,
          total_rooms_count: hotelInfo.statistics.total_rooms_count || 0,
          qr_rooms_count: hotelInfo.statistics.qr_rooms_count || 0,
          rooms_without_qr: hotelInfo.statistics.rooms_without_qr || 0,

          // Moyennes et taux
          avg_greenmiles_per_stay: hotelInfo.statistics.avg_greenmiles_per_stay || 0,
          engagement_rate: hotelInfo.statistics.engagement_rate || 0,
          reward_claim_rate: hotelInfo.statistics.reward_claim_rate || 0,
          reward_usage_rate: hotelInfo.statistics.reward_usage_rate || 0,

          // Métadonnées
          period: hotelInfo.statistics.period || period,
        });

        // Traitement des actions
        let actionsArray = [];
        if (actionsData.success === "OK") {
          actionsArray = actionsData.result || [];
          setActions(actionsArray);

          // Configurer les types d'actions disponibles
          const actionTypes = getAvailableActionTypes(actionsArray);
          setAvailableActionTypes(actionTypes);

          // Étendre automatiquement la sélection si "tout était sélectionné"
          setSelectedActionTypes((prev) => {
            if (prev.length === 0) {
              // Initialisation: sélectionner tous les types
              return getAllActionTypes(actionsArray);
            }
            const prevSet = new Set(prev);
            const allTypesNow = getAllActionTypes(actionsArray);
            // Si avant on avait tous les types disponibles sélectionnés, on ajoute les nouveaux
            const hadAllBefore = prevSet.size >= availableActionTypes.length && availableActionTypes.every((t) => prevSet.has(t.value));
            if (hadAllBefore) {
              return allTypesNow;
            }
            return prev;
          });
        } else {
          console.warn("Erreur lors de la récupération des actions:", actionsData.message);
          setActions([]);
          setAvailableActionTypes([]);
        }

        // Traitement des rewards
        let rewardsArray = [];
        if (rewardsData.success === "OK") {
          rewardsArray = rewardsData.result || [];
          setRewards(rewardsArray);

          // Configurer les types de rewards disponibles
          const rewardTypes = getAvailableRewardTypes(rewardsArray);
          setAvailableRewardTypes(rewardTypes);

          // Étendre automatiquement la sélection si "tout était sélectionné"
          setSelectedRewardTypes((prev) => {
            if (prev.length === 0) {
              // Initialisation: sélectionner tous les types
              return getAllRewardTypes(rewardsArray);
            }
            const prevSet = new Set(prev);
            const allTypesNow = getAllRewardTypes(rewardsArray);
            const hadAllBefore = prevSet.size >= availableRewardTypes.length && availableRewardTypes.every((t) => prevSet.has(t.value));
            if (hadAllBefore) {
              return allTypesNow;
            }
            return prev;
          });
        } else {
          console.warn("Erreur lors de la récupération des rewards:", rewardsData.message);
          setRewards([]);
          setAvailableRewardTypes([]);
        }
      } catch (err) {
        // Ignorer les erreurs d'annulation
        if (err.name === "AbortError") {
          return;
        }

        console.error("Error fetching hotel data:", err);
        setError(err.message || "Erreur lors de la récupération des données");
        setHotel(null);
        setRooms([]);
        setActions([]);
        setRewards([]);
        setStatistics(null);
      } finally {
        setLoading(false);
        setPeriodLoading(false);
      }
    },
    [hotelId, stableToken]
  );

  // Fonction pour changer de période
  const handlePeriodChange = useCallback(
    (newPeriod) => {
      setSelectedPeriod(newPeriod);
      fetchHotelData(newPeriod, true); // true indique que c'est un changement de période
    },
    [fetchHotelData]
  );

  // Fonction pour gérer le tri des chambres QR
  const handleSort = useCallback((column) => {
    setSortState((prevState) => {
      const newDirection = getQRSortDirection(prevState.column, column, prevState.direction);
      return { column, direction: newDirection };
    });
  }, []);

  // Fonctions pour gérer le filtrage des actions par type
  const handleActionTypeToggle = useCallback((type) => {
    setSelectedActionTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  }, []);

  const handleClearAllActionTypes = useCallback(() => {
    setSelectedActionTypes([]);
  }, []);

  const handleSelectAllActionTypes = useCallback(() => {
    setSelectedActionTypes(getAllActionTypes(actions));
  }, [actions]);

  // Fonctions pour gérer le filtrage des rewards par type
  const handleRewardTypeToggle = useCallback((type) => {
    setSelectedRewardTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  }, []);

  const handleClearAllRewardTypes = useCallback(() => {
    setSelectedRewardTypes([]);
  }, []);

  const handleSelectAllRewardTypes = useCallback(() => {
    setSelectedRewardTypes(getAllRewardTypes(rewards));
  }, [rewards]);

  // Fonctions pour gérer le toggle du statut hidden des actions
  const toggleActionHidden = useCallback(
    async (actionId, currentHidden) => {
      try {
        const newHidden = !currentHidden;

        const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/actions/${actionId}/toggle-hidden`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stableToken}`,
          },
          body: JSON.stringify({ hidden: newHidden }),
        });

        const data = await response.json();

        if (data.success === "OK") {
          // Mettre à jour l'état local des actions
          setActions((prevActions) => prevActions.map((action) => (action.acId === actionId ? { ...action, acVisible: !newHidden } : action)));

          // Refetch les données pour mettre à jour les statistiques
          fetchHotelData();

          return { success: true, message: data.message };
        } else {
          throw new Error(data.message || "Erreur lors de la mise à jour");
        }
      } catch (error) {
        console.error("Error toggling action hidden status:", error);
        return { success: false, message: error.message };
      }
    },
    [hotelId, stableToken, fetchHotelData]
  );

  // Fonctions pour gérer le toggle du statut hidden des rewards
  const toggleRewardHidden = useCallback(
    async (rewardId, currentHidden) => {
      try {
        const newHidden = !currentHidden;

        const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/rewards/${rewardId}/toggle-hidden`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stableToken}`,
          },
          body: JSON.stringify({ hidden: newHidden }),
        });

        const data = await response.json();

        if (data.success === "OK") {
          // Mettre à jour l'état local des rewards
          setRewards((prevRewards) => prevRewards.map((reward) => (reward.rwId === rewardId ? { ...reward, rwVisible: !newHidden } : reward)));

          // Refetch les données pour mettre à jour les statistiques
          fetchHotelData();

          return { success: true, message: data.message };
        } else {
          throw new Error(data.message || "Erreur lors de la mise à jour");
        }
      } catch (error) {
        console.error("Error toggling reward hidden status:", error);
        return { success: false, message: error.message };
      }
    },
    [hotelId, stableToken, fetchHotelData]
  );

  // Fonctions pour gérer le filtrage par statut de stay
  const toggleStayStatusFilter = useCallback(
    (status) => {
      setSelectedStayStatuses((prev) => {
        if (prev.includes(status)) {
          return prev.filter((s) => s !== status);
        } else {
          return [...prev, status];
        }
      });
    },
    []
  );

  const clearAllStayStatusFilters = useCallback(() => {
    setSelectedStayStatuses([]);
  }, []);

  const selectAllStayStatusFilters = useCallback(() => {
    if (rooms.length > 0) {
      const allStayStatuses = getAllStayStatusValues(rooms);
      setSelectedStayStatuses(allStayStatuses);
    }
  }, [rooms]);

  // Données filtrées et triées (le tri des chambres se fait dans RoomsTable après le filtrage)
  const filteredActions = sortActions(filterActionsByType(actions, selectedActionTypes), ACTION_SORT_COLUMNS.USAGE_COUNT, "desc");
  const filteredRewards = sortRewards(filterRewardsByType(rewards, selectedRewardTypes), REWARD_SORT_COLUMNS.USAGE_COUNT, "desc");

  const updateRoomTypeForRoom = useCallback(
    async (roomId, roomTypeId) => {
      if (!hotelId || !stableToken) {
        return { success: false, message: "Authentification requise" };
      }
      try {
        const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/rooms/${roomId}/room-type`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stableToken}`,
          },
          body: JSON.stringify({ roomTypeId }),
        });
        const data = await response.json();
        if (!response.ok || data?.success !== true) {
          throw new Error(data?.message || "Erreur lors de la mise à jour");
        }

        const updatedTypeId = data?.result?.roomTypeId ?? null;
        const updatedTypeName = data?.result?.roomTypeName ?? null;
        const updatedTypeColor = data?.result?.roomTypeColor ? normalizeRoomTypeColor(data.result.roomTypeColor) : null;

        setRooms((prevRooms) =>
          prevRooms.map((room) =>
            room.rId === roomId
              ? { ...room, roomTypeId: updatedTypeId, roomType: updatedTypeName, roomTypeColor: updatedTypeColor }
              : room
          )
        );

        return { success: true, message: "Type de chambre mis à jour" };
      } catch (error) {
        console.error("Error updating room type:", error);
        return { success: false, message: error.message || "Erreur lors de la mise à jour" };
      }
    },
    [hotelId, stableToken]
  );

  // 🔧 Chargement initial SEULEMENT - Éviter les re-fetches constants
  useEffect(() => {
    if (!isInitializedRef.current && stableToken && hotelId) {
      fetchHotelData();
      isInitializedRef.current = true;
    }
  }, [hotelId, stableToken, fetchHotelData]);

  useEffect(() => {
    if (hotelId && stableToken) {
      fetchRoomTypes();
    }
  }, [hotelId, stableToken, fetchRoomTypes]);

  // Nettoyage des requêtes en cours lors du démontage
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const refetch = useCallback(() => {
    fetchHotelData(selectedPeriod, false);
    fetchRoomTypes();
  }, [fetchHotelData, selectedPeriod, fetchRoomTypes]);

  return {
    hotel,
    rooms,
    actions: filteredActions,
    rewards: filteredRewards,
    statistics,
    loading,
    periodLoading,
    error,
    // Période
    selectedPeriod,
    handlePeriodChange,
    // Filtrage par statut de stay
    selectedStayStatuses,
    toggleStayStatusFilter,
    clearAllStayStatusFilters,
    selectAllStayStatusFilters,
    // Tri des chambres
    sortState,
    handleSort,
    // Filtrage des actions par type
    selectedActionTypes,
    availableActionTypes,
    handleActionTypeToggle,
    handleClearAllActionTypes,
    handleSelectAllActionTypes,
    // Filtrage des rewards par type
    selectedRewardTypes,
    availableRewardTypes,
    handleRewardTypeToggle,
    handleClearAllRewardTypes,
    handleSelectAllRewardTypes,
    toggleActionHidden,
    toggleRewardHidden,
    refetch,
    roomTypes,
    roomTypesLoading,
    updateRoomTypeForRoom,
    refetchRoomTypes: fetchRoomTypes,
  };
};
